// walletMonitor.js
/* eslint-disable */
import { getAssociatedTokenAddressSync, TOKEN_2022_PROGRAM_ID, unpackAccount } from "@solana/spl-token"
import { Connection, PublicKey } from "@solana/web3.js"

const connection_string = "https://mainnet.helius-rpc.com/?api-key=c9018c54-1f79-4563-9915-3768ffef0b14"

// Сколько кошельков класть в 1 WS-коннект
// 1 wallet = 2 подписки (SOL + ATA)
// + 1 подписка onLogs на весь mint
// safe: 45-48
const WALLETS_PER_CONNECTION = 45

// Free Helius: до 5 WS-соединений. Если платный, можно поднять.
const MAX_CONNECTIONS = 5

function chunkArray(arr, size) {
  const out = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

export function startMonitoring({ wallets, tokenMint }, EventBus) {
  if (!tokenMint || typeof tokenMint !== "string") {
    console.error("Invalid tokenMint:", tokenMint)
    return () => {}
  }

  const TOKEN_MINT = new PublicKey(tokenMint)

  const walletChunks = chunkArray(wallets, WALLETS_PER_CONNECTION).slice(0, MAX_CONNECTIONS)

  if (walletChunks.length * WALLETS_PER_CONNECTION < wallets.length) {
    console.warn(
      `Wallets=${wallets.length}. На текущих настройках можно мониторить до ${
        walletChunks.length * WALLETS_PER_CONNECTION
      } кошельков. Остальные будут проигнорированы.`
    )
  }

  const monitors = walletChunks.map((chunk, idx) =>
    createMonitorConnection({
      idx,
      wallets: chunk,
      TOKEN_MINT,
      EventBus,
    })
  )

  // Вернем стоппер
  return function stopAll() {
    monitors.forEach((m) => m.stop())
  }
}

function createMonitorConnection({ idx, wallets, TOKEN_MINT, EventBus }) {
  const connection = new Connection(connection_string, "processed")

  // Храним subscriptionId чтобы отписаться
  const subs = []

  // Подписка на логи mint (1 штука на коннект)
  const logsSubId = connection.onLogs(TOKEN_MINT, async ({ logs, signature, err }) => {
    if (err) return
    try {
      await parseTransaction(connection, signature, TOKEN_MINT, EventBus)
    } catch (error) {
      console.error("Ошибка при парсинге транзакции:", error, logs)
    }
  })
  subs.push({ type: "logs", id: logsSubId })

  wallets.forEach((wallet) => {
    const account = new PublicKey(wallet.publicKey)
    const tokenAccount = getAssociatedTokenAddressSync(TOKEN_MINT, account, false, TOKEN_2022_PROGRAM_ID)

    // SOL баланс
    const solSubId = connection.onAccountChange(account, (accountInfo) => {
      const solBalance = accountInfo.lamports / 1e9
      EventBus.emit("sol-balance-updated", { wallet, balance: solBalance })
    })
    subs.push({ type: "account", id: solSubId })

    // Token ATA баланс
    const tokenSubId = connection.onAccountChange(tokenAccount, (tokenAccountInfo) => {
      try {
        const accountInfo = unpackAccount(tokenAccount, tokenAccountInfo, TOKEN_2022_PROGRAM_ID) // важно: сюда нужно передавать tokenAccount
        if (accountInfo.mint.toString() !== TOKEN_MINT.toString()) return
        const tokenBalance = Number(accountInfo.amount) / 1e6
        EventBus.emit("token-balance-updated", { wallet, balance: tokenBalance })
      } catch (err) {
        console.log(err)
        // ATA может не существовать, это нормально
        // Можно логировать только неожиданные ошибки
      }
    })
    subs.push({ type: "account", id: tokenSubId })
  })

  console.log(`[monitor#${idx}] wallets=${wallets.length}, subs=${subs.length}`)

  return {
    connection,
    stop: async () => {
      // Важно: корректно отписываемся, иначе при повторном старте подписки копятся
      for (const s of subs) {
        try {
          if (s.type === "account") await connection.removeAccountChangeListener(s.id)
          if (s.type === "logs") await connection.removeOnLogsListener(s.id)
        } catch (e) {
          // ignore
        }
      }
      try {
        // у web3.js нет прямого "close ws", но после удаления всех листенеров оно обычно само глохнет
        // главное не держать ссылки и не плодить новые без stop()
      } catch (e) {
        // ignore
      }
    },
  }
}

const PUMPFUN_PROGRAM_IDS = new Set([
  "6EF8rrecthR5Dkzon8Nwu78hRvfCKubJ14M5uBEwF6P",
  "BSfD6SHZigAfDWSjzD5Q41jw8LmKwtmjskCH9Cau9Aih",
  "PSwapMdSai8tjrEXcxFeQth87xC4rRsa4VA5mhGhXkP",
  "PumpFunuYiVXKEh6nkJulFPmqU6hQmzHME1Gxpn7U8y",
])

function hasPumpfunTrade(tx) {
  for (const ix of tx.transaction.message.instructions) {
    if (ix.programId && PUMPFUN_PROGRAM_IDS.has(String(ix.programId))) return true
  }
  if (tx.meta?.innerInstructions) {
    for (const inner of tx.meta.innerInstructions) {
      for (const ix of inner.instructions) {
        if (ix.programId && PUMPFUN_PROGRAM_IDS.has(String(ix.programId))) return true
      }
    }
  }
  return false
}

function extractTradesFromTx(tx, tokenMint, signature) {
  const mintStr = String(tokenMint)
  const accountKeys = tx.transaction.message.accountKeys.map((k) =>
    typeof k === "object" && k.pubkey ? k.pubkey.toString() : k.toString()
  )

  // Build map: owner -> { pre, post } token amounts
  const ownerTokens = {}
  for (const b of tx.meta.preTokenBalances || []) {
    if (b.mint !== mintStr) continue
    ownerTokens[b.owner] = ownerTokens[b.owner] || { pre: 0, post: 0 }
    ownerTokens[b.owner].pre = b.uiTokenAmount?.uiAmount || 0
  }
  for (const b of tx.meta.postTokenBalances || []) {
    if (b.mint !== mintStr) continue
    ownerTokens[b.owner] = ownerTokens[b.owner] || { pre: 0, post: 0 }
    ownerTokens[b.owner].post = b.uiTokenAmount?.uiAmount || 0
  }

  const trades = []
  for (const [owner, { pre, post }] of Object.entries(ownerTokens)) {
    const tokenDiff = post - pre
    if (tokenDiff === 0) continue

    // Find this owner in accountKeys for SOL balance lookup
    const ownerIdx = accountKeys.indexOf(owner)
    if (ownerIdx === -1) continue

    // Skip pool/bonding curve accounts — they're not signers
    const keyObj = tx.transaction.message.accountKeys[ownerIdx]
    const isSigner = typeof keyObj === "object" && keyObj.signer === true
    if (!isSigner) continue

    const isBuy = tokenDiff > 0
    const solChange = Math.abs(tx.meta.preBalances[ownerIdx] - tx.meta.postBalances[ownerIdx]) / 1e9

    trades.push({
      signature,
      wallet: owner,
      token_amount: Math.abs(tokenDiff).toFixed(2),
      sol_amount: parseFloat(solChange.toFixed(4)),
      type: isBuy ? "buy" : "sell",
      timestamp: tx.blockTime * 1000,
    })
  }

  return trades
}

async function parseTransaction(connection, signature, tokenMint, EventBus) {
  let tx = null
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) await new Promise((r) => setTimeout(r, 600))
    try {
      tx = await connection.getParsedTransaction(signature, {
        maxSupportedTransactionVersion: 0,
        commitment: "confirmed",
      })
    } catch (e) {}
    if (tx) break
  }
  if (!tx) return

  if (!hasPumpfunTrade(tx)) return

  const trades = extractTradesFromTx(tx, tokenMint, signature)
  for (const trade of trades) {
    EventBus.emit("transaction", trade)
  }
}

export async function fetchPastTransactions(tokenMint, limit = 50) {
  const conn = new Connection(connection_string, "confirmed")
  const mintPk = new PublicKey(tokenMint)

  const signatures = await conn.getSignaturesForAddress(mintPk, { limit })
  const trades = []

  const BATCH_SIZE = 25
  for (let i = 0; i < signatures.length; i += BATCH_SIZE) {
    const batch = signatures.slice(i, i + BATCH_SIZE)
    const txPromises = batch.map((sig) =>
      conn
        .getParsedTransaction(sig.signature, {
          maxSupportedTransactionVersion: 0,
          commitment: "confirmed",
        })
        .catch(() => null)
    )

    const txResults = await Promise.all(txPromises)

    // Small pause between batches to avoid rate limits
    if (i + BATCH_SIZE < signatures.length) {
      await new Promise((r) => setTimeout(r, 100))
    }

    for (let j = 0; j < txResults.length; j++) {
      const tx = txResults[j]
      if (!tx) continue

      const sig = batch[j].signature

      if (!hasPumpfunTrade(tx)) continue

      const txTrades = extractTradesFromTx(tx, tokenMint, sig)
      trades.push(...txTrades)
    }
  }

  console.log("[trades] fetched", signatures.length, "signatures,", trades.length, "trades parsed")
  trades.sort((a, b) => b.timestamp - a.timestamp)
  return trades
}
